import {
  foreignKey,
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import type { MediaAssetSlim } from "@/lib/media";
import type { MusicAttachment } from "@/lib/music/attachment";
import type { CommentMedia } from "@/validations/comment";
import { activity } from "./activities";
import { user } from "./auth";

// ──────────────────────────────────────────────────────────────────────────────
// VISIBILITY & STATUS
// ──────────────────────────────────────────────────────────────────────────────

// public → anyone; private → buddies; close_friends → the author's
// close-friends list (src/lib/close-friends.ts).
export const ACTIVITY_POST_VISIBILITY = [
  "public",
  "private",
  "close_friends",
] as const;
export type ActivityPostVisibility = (typeof ACTIVITY_POST_VISIBILITY)[number];

export const ACTIVITY_POST_STATUS = ["published", "deleted"] as const;
export type ActivityPostStatus = (typeof ACTIVITY_POST_STATUS)[number];

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY POST (main table)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Stores activity-specific posts (e.g., Tennis match logs).
 * `data` is a flexible JSON column storing template-specific details like
 * mode, scores, and participant positions on a court visualization.
 */
export const activityPost = sqliteTable(
  "activity_post",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    authorId: text("author_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id, { onDelete: "restrict" }),

    // Flexible JSON data for template-specific content
    // Example: { mode: "singles", scores: ["6-4"], positions: { userId: "top-court" } }
    data: text("data", { mode: "json" }).$type<Record<string, unknown>>(),

    // Optional caption/description for the activity
    assistantName: text("assistant_name"),
    assistantHandle: text("assistant_handle"),
    content: text("content"),

    visibility: text("visibility")
      .notNull()
      .$type<ActivityPostVisibility>()
      .default("public"),

    status: text("status")
      .notNull()
      .$type<ActivityPostStatus>()
      .default("published"),

    ...timestamps,
  },
  (t) => [
    index("activity_post_author_idx").on(t.authorId),
    index("activity_post_activity_idx").on(t.activityId),
    index("activity_post_status_idx").on(t.status),
    index("activity_post_visibility_idx").on(t.visibility),
    index("activity_post_created_at_idx").on(t.createdAt),
  ],
);

export type ActivityPost = typeof activityPost.$inferSelect;
export type ActivityPostInsert = typeof activityPost.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY POST PARTICIPANT
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Stores tagged participants for an activity post.
 * Used to query feed items where a user is involved.
 */
export const activityPostParticipant = sqliteTable(
  "activity_post_participant",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activityPostId: integer("activity_post_id")
      .notNull()
      .references(() => activityPost.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_post_participant_unique").on(
      t.activityPostId,
      t.userId,
    ),
    index("activity_post_participant_post_idx").on(t.activityPostId),
    index("activity_post_participant_user_idx").on(t.userId),
  ],
);

export type ActivityPostParticipant =
  typeof activityPostParticipant.$inferSelect;
export type ActivityPostParticipantInsert =
  typeof activityPostParticipant.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY POST LIKE
// ──────────────────────────────────────────────────────────────────────────────

export const activityPostLike = sqliteTable(
  "activity_post_like",
  {
    activityPostId: integer("activity_post_id")
      .notNull()
      .references(() => activityPost.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_post_like_unique").on(t.activityPostId, t.userId),
    index("activity_post_like_post_idx").on(t.activityPostId),
    index("activity_post_like_user_idx").on(t.userId),
  ],
);

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY POST COMMENT
// ──────────────────────────────────────────────────────────────────────────────

export const activityPostComment = sqliteTable(
  "activity_post_comment",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activityPostId: integer("activity_post_id")
      .notNull()
      .references(() => activityPost.id, { onDelete: "cascade" }),

    authorId: text("author_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    parentCommentId: integer("parent_comment_id"),
    rootCommentId: integer("root_comment_id"),
    depth: integer("depth").notNull().default(0),

    assistantName: text("assistant_name"),
    assistantHandle: text("assistant_handle"),
    content: text("content").notNull(),
    voiceAsset: text("voice_asset", {
      mode: "json",
    }).$type<MediaAssetSlim | null>(),
    voiceDurationMs: integer("voice_duration_ms"),
    voiceWaveform: text("voice_waveform", { mode: "json" }).$type<
      number[] | null
    >(),
    media: text("media", { mode: "json" }).$type<CommentMedia | null>(),
    /** A song reply (catalog snapshot, server-resolved); null = none. */
    music: text("music", { mode: "json" }).$type<MusicAttachment | null>(),
    isPinned: integer("is_pinned", { mode: "boolean" })
      .notNull()
      .default(false),
    pinnedAt: integer("pinned_at", { mode: "timestamp" }),
    pinnedById: text("pinned_by_id").references(() => user.id, {
      onDelete: "set null",
    }),

    ...timestamps,
  },
  (t) => [
    index("activity_post_comment_post_idx").on(t.activityPostId),
    index("activity_post_comment_author_idx").on(t.authorId),
    index("activity_post_comment_parent_idx").on(t.parentCommentId),
    index("activity_post_comment_root_idx").on(t.rootCommentId),
    index("activity_post_comment_pinned_idx").on(t.activityPostId, t.isPinned),
    foreignKey({
      name: "activity_post_comment_parent_fk",
      columns: [t.parentCommentId],
      foreignColumns: [t.id],
    }).onDelete("cascade"),
  ],
);

export type ActivityPostComment = typeof activityPostComment.$inferSelect;
export type ActivityPostCommentInsert = typeof activityPostComment.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// ACTIVITY POST COMMENT LIKE
// ──────────────────────────────────────────────────────────────────────────────

export const activityPostCommentLike = sqliteTable(
  "activity_post_comment_like",
  {
    commentId: integer("comment_id")
      .notNull()
      .references(() => activityPostComment.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_post_comment_like_unique").on(t.commentId, t.userId),
    index("activity_post_comment_like_comment_idx").on(t.commentId),
    index("activity_post_comment_like_user_idx").on(t.userId),
  ],
);
